import {dateField, field, LocalisableEntityData, transientField} from "@fedeltapointofsale/data-client";
import {DateTimeRangeData} from "../../datetime/date-time-range.data";
import {DateTimeUtil} from "../../framework/utils/date-time-util";
import {DatePipe} from "@angular/common";

export enum PricingScheduleFrequency {
    MINUTES = <any> "minutes",
    HOURS = <any> "hours",
    DAYS = <any> "days",
    WEEKS = <any> "weeks"
}

export class PricingScheduleSummaryData<T> extends DateTimeRangeData<T> {
    @field frequency: number = 0;
    @field duration: number;

    get recurring(): boolean {
        return this.frequency && this.start && this.finish && this.finish.getTime() !== this.start.getTime();
    }

    get frequencyValue(): string {
        const value: number = PricingScheduleData.divideFrequency(this.frequency).value;
        return value == 1 ? "" : value.toString();
    }

    get frequencyUnit(): string {
        const pricingScheduleFrequency: PricingScheduleFrequency = PricingScheduleData.divideFrequency(this.frequency).unit;
        const unit: string = this.frequencyValue ? pricingScheduleFrequency.toString()
            : pricingScheduleFrequency == PricingScheduleFrequency.MINUTES ? "minute"
                : pricingScheduleFrequency == PricingScheduleFrequency.HOURS ? "hour"
                    : pricingScheduleFrequency == PricingScheduleFrequency.DAYS ? "day"
                        : pricingScheduleFrequency == PricingScheduleFrequency.WEEKS ? "week"
                            : undefined;
        return "{{wbo.pricing.schedule.message.period." + unit + "}}";
    }

    get durationHours(): number {
        return DateTimeUtil.millisToHours(this.duration);
    }

    get durationHoursUnit(): string {
        return this.durationHours == 1 ? "{{wbo.pricing.schedule.message.period.hour}}" : "{{wbo.pricing.schedule.message.period.hours}}";
    }

    get durationMinutes(): number {
        return DateTimeUtil.millisToMinutes(this.duration);
    }

    get durationMinutesUnit(): string {
        return this.durationMinutes == 1 ? "{{wbo.pricing.schedule.message.period.minute}}" : "{{wbo.pricing.schedule.message.period.minutes}}";
    }

    get startDateSt(): string {
        return new DatePipe("en-GB").transform(this.start, "EEE, MMM d, yyyy");
    }

    get startTimeSt(): string {
        return new DatePipe("en-US").transform(this.start, "h:mm a");
    }

    get finishDateSt(): string {
        return new DatePipe("en-GB").transform(this.finish, "EEE, MMM d, yyyy");
    }

    get finishTimeSt(): string {
        return new DatePipe("en-US").transform(this.finish, "h:mm a");
    }

    static getDescriptionMessage(schedule: PricingScheduleSummaryData<any>): string {
        return schedule.recurring
            ? (!schedule.durationMinutes ? "wbo.pricing.schedule.message.description.recurring.h"
                : !schedule.durationHours ? "wbo.pricing.schedule.message.description.recurring.m"
                    : "wbo.pricing.schedule.message.description.recurring.hm")
            : (!schedule.durationMinutes ? "wbo.pricing.schedule.message.description.nonRecurring.h"
                : !schedule.durationHours ? "wbo.pricing.schedule.message.description.nonRecurring.m"
                    : "wbo.pricing.schedule.message.description.nonRecurring.hm");
    }
}

export abstract class PricingScheduleData<T> extends LocalisableEntityData<T> {
    @dateField start: Date;
    @dateField finish: Date;
    @field frequency: number = 0;
    @field duration: number;
    @transientField recurring: boolean = false;

    _firstFinish: Date;
    private _frequencyUnit: PricingScheduleFrequency = PricingScheduleFrequency.MINUTES;

    @transientField get startDate(): Date {
        return this.start;
    }

    set startDate(start: Date) {
        this.start = DateTimeUtil.setDate(this.start, start);
        this.calculateDuration();
    }

    @transientField get startTime(): Date {
        return this.start;
    }

    set startTime(start: Date) {
        this.start = DateTimeUtil.setTime(this.start, start);
        this.calculateDuration();
    }

    @transientField get finishDate(): Date {
        return this.finish;
    }

    set finishDate(finish: Date) {
        this.finish = DateTimeUtil.setDate(this.finish, finish);
    }

    @transientField get finishTime(): Date {
        return this.finish;
    }

    set finishTime(finish: Date) {
        this.finish = DateTimeUtil.setTime(this.finish, finish);
    }

    @transientField get frequencyUnit(): PricingScheduleFrequency {
        return this._frequencyUnit;
    }

    set frequencyUnit(value: PricingScheduleFrequency) {
        this._frequencyUnit = value;
        this.calculateFrequency();
    }

    private _frequencyValue: number;

    @transientField get frequencyValue(): number {
        return this._frequencyValue;
    }

    set frequencyValue(value: number) {
        this._frequencyValue = value;
        this.calculateFrequency();
    }

    @transientField get firstFinishTime(): Date {
        return this._firstFinish;
    }

    set firstFinishTime(firstFinishTime: Date) {
        this._firstFinish = firstFinishTime;
        this.calculateDuration();
    }

    @transientField get durationHHMM(): string {
        return DateTimeUtil.millisToHHMM(this.duration);
    }

    set durationHHMM(value: string) {
    }

    static divideFrequency(frequency: number): { value: number, unit: PricingScheduleFrequency } {
        if (!frequency) {
            return {value: undefined, unit: undefined};
        }
        if (frequency % DateTimeUtil.MS_WEEK === 0) {
            return {value: frequency / DateTimeUtil.MS_WEEK, unit: PricingScheduleFrequency.WEEKS};
        }
        if (frequency % DateTimeUtil.MS_DAY === 0) {
            return {value: frequency / DateTimeUtil.MS_DAY, unit: PricingScheduleFrequency.DAYS};
        }
        if (frequency % DateTimeUtil.MS_HOUR === 0) {
            return {value: frequency / DateTimeUtil.MS_HOUR, unit: PricingScheduleFrequency.HOURS};
        }
        return {value: Math.floor(frequency / DateTimeUtil.MS_MINUTE), unit: PricingScheduleFrequency.MINUTES};
    }

    override onLoad(): void {
        if (this.start && this.duration) {
            this._firstFinish = new Date(this.start.getTime() + this.duration);
        }
        if (this.start && this.finish && this.finish.getTime() !== this.start.getTime()) {
            this.recurring = true;
        }
        ({
            value: this._frequencyValue,
            unit: this._frequencyUnit
        } = PricingScheduleData.divideFrequency(this.frequency));
        super.onLoad();
    }

    calculateDuration(): void {
        if (this._firstFinish && this.start) {
            let firstFinish: Date = new Date(this._firstFinish.getTime());
            DateTimeUtil.setDate(firstFinish, this.start);
            if (firstFinish.getTime() < this.start.getTime()) {
                firstFinish = new Date(firstFinish.getTime() + DateTimeUtil.MS_DAY);
            }
            this.duration = firstFinish.getTime() - this.start.getTime();
        }
    }

    calculateFrequency(): void {
        if (this._frequencyValue !== undefined && this._frequencyUnit !== undefined) {
            this.frequency = this._frequencyValue * (
                this._frequencyUnit === PricingScheduleFrequency.WEEKS ? DateTimeUtil.MS_WEEK
                    : this._frequencyUnit === PricingScheduleFrequency.DAYS ? DateTimeUtil.MS_DAY
                    : this._frequencyUnit === PricingScheduleFrequency.HOURS ? DateTimeUtil.MS_HOUR
                        : DateTimeUtil.MS_MINUTE
            );
        }
    }
}
